/**
 * In-game HUD (implements HudApi): health/stamina, evolution meter with size-up notches,
 * mutation meter, objective, toasts, counters, boss bar, action bar with cooldown rings,
 * evolve prompt (hold-to-fill ring), damage/threat/low-HP vignettes.
 * DOM writes are skipped when values don't change so stages can call these every frame.
 */
import type { EvoNode, EvoPath, ActionLabelKey } from '@core/tree';
import type { HudApi, ToastKind } from '@core/services';
import type { InputDevice } from '@core/input';
import { clamp01, el, escapeHtml } from '../dom';
import { BRANCH_ICONS, ICONS } from '../icons';
import { hintHtml } from '../keys';

const PAD_HINTS: Record<ActionLabelKey, string> = { attack: 'X', dash: 'B', jump: 'A', ability: 'Y', interact: 'Back' };
const ACTION_ORDER: ActionLabelKey[] = ['interact', 'ability', 'jump', 'dash', 'attack'];
const TOAST_ICONS: Record<ToastKind, string> = {
  info: ICONS.dna,
  good: ICONS.heart,
  bad: ICONS.warn,
  trait: ICONS.star,
  growth: ICONS.dna,
};
/** Size-up notches on the evolution meter (growth fractions). */
export const GROWTH_NOTCHES = [1 / 3, 2 / 3, 1];

class Bar {
  readonly root: HTMLElement;
  private fill: HTMLElement;
  private ghost: HTMLElement;
  private last = -1;
  constructor(cls: string, icon?: string) {
    this.root = el('div', `hbar ${cls}`, `<i class="ghost"></i><i class="fill"></i>${cls.includes('health') ? '<div class="segs"></div>' : ''}${icon ? `<span class="ico">${icon}</span>` : ''}`);
    this.ghost = this.root.querySelector('.ghost')!;
    this.fill = this.root.querySelector('.fill')!;
  }
  set(frac: number): boolean {
    const f = Math.round(clamp01(frac) * 1000) / 1000;
    if (f === this.last) return false;
    const dropped = f < this.last;
    this.last = f;
    this.fill.style.transform = `scaleX(${f})`;
    // Ghost trails behind on damage, snaps on heal.
    this.ghost.style.transition = dropped ? '' : 'none';
    this.ghost.style.transform = `scaleX(${f})`;
    return dropped;
  }
}

export class Hud implements HudApi {
  readonly root = el('div', 'hud');
  private tl = el('div', 'hud-tl');
  private nameplate = el('div', 'nameplate');
  private health = new Bar('health', ICONS.heart);
  private hpText = el('span', 'hp-val');
  private stamina = new Bar('stamina hidden', ICONS.bolt);
  private evo = el('div', 'evo-meter');
  private evoFill: HTMLElement;
  private evoPct: HTMLElement;
  private notches: HTMLElement[] = [];
  private mut = el('div', 'mut-meter hidden');
  private mutTip = el('div', 'mut-tip');
  private objective = el('div', 'objective');
  private objectiveText = '';
  private toasts = el('div', 'toasts');
  private tr = el('div', 'hud-tr');
  private btnRow = el('div', 'row');
  private chips = el('div', 'row chips');
  private counters = new Map<string, { el: HTMLElement; val: string }>();
  private boss = el('div', 'boss', `<div class="bn">${ICONS.skull}<span></span></div>`);
  private bossBarEl = new Bar('boss-hp');
  private bossName = '';
  private actionBar = el('div', 'action-bar');
  private actionEls = new Map<ActionLabelKey, { root: HTMLElement; cd: HTMLElement; frac: number }>();
  private labels: Partial<Record<ActionLabelKey, string>> = {};
  private evolve = el('div', 'evolve-prompt hidden');
  private evolveKey = '';
  private evolveHold = -1;
  private vDamage = el('div', 'vignette damage');
  private vThreat = el('div', 'vignette threat');
  private vLow = el('div', 'vignette lowhp');
  private threatBadge = el('div', 'threat-badge', `${ICONS.warn}<span>Danger</span>`);
  private growthFrac = -1;
  private affinityKey = '';
  private leadKey = '';
  private lowHp = false;
  private lastHp = 1;
  private regenTimer?: ReturnType<typeof setTimeout>;
  private threat = -1;
  private device: InputDevice = 'keyboard';
  private lastToast = { text: '', at: 0 };
  onEvolveTap?: (down: boolean) => void;
  onPauseTap?: () => void;
  onFullscreenTap?: () => void;
  onExplorerTap?: () => void;
  private explorerBtn?: HTMLButtonElement;
  onActions?: (labels: Partial<Record<ActionLabelKey, string>>) => void;
  onCooldown?: (a: ActionLabelKey, f: number) => void;
  onGrowthNotch?: (index: number) => void;
  onEvolveVisible?: (v: boolean) => void;

  constructor() {
    this.evo.innerHTML = `<div class="lbl"><span>Evolution</span><b>0%</b></div><div class="track"><div class="fill"></div></div>`;
    this.evoFill = this.evo.querySelector('.fill')!;
    this.evoPct = this.evo.querySelector('.lbl b')!;
    const track = this.evo.querySelector('.track') as HTMLElement;
    GROWTH_NOTCHES.forEach((f, i) => {
      const n = el('i', `notch${i === GROWTH_NOTCHES.length - 1 ? ' last' : ''}`);
      n.style.left = `${f * 100}%`;
      track.append(n);
      this.notches.push(n);
    });
    this.mut.append(this.mutTip);
    const showTip = (v: boolean) => this.mutTip.classList.toggle('show', v);
    this.mut.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && showTip(true));
    this.mut.addEventListener('pointerleave', () => showTip(false));
    this.mut.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      showTip(!this.mutTip.classList.contains('show'));
    });
    this.health.root.append(this.hpText);
    this.tl.append(this.nameplate, this.health.root, this.stamina.root, this.evo, this.mut);

    const pause = el('button', 'hud-btn pause-btn', ICONS.pause);
    pause.setAttribute('aria-label', 'Pause');
    pause.addEventListener('click', () => this.onPauseTap?.());
    this.btnRow.append(pause);
    this.tr.append(this.btnRow, this.chips, this.threatBadge);

    this.boss.append(this.bossBarEl.root);
    this.evolve.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.evolve.setPointerCapture(e.pointerId);
      this.evolve.classList.add('holding');
      this.onEvolveTap?.(true);
    });
    const up = () => {
      this.evolve.classList.remove('holding');
      this.onEvolveTap?.(false);
    };
    this.evolve.addEventListener('pointerup', up);
    this.evolve.addEventListener('pointercancel', up);
    this.vLow.style.display = 'none';
    this.vThreat.style.opacity = '0';

    this.root.append(this.vLow, this.vThreat, this.vDamage, this.tl, this.objective, this.tr, this.toasts, this.boss, this.evolve, this.actionBar);
  }

  /** Stage-explorer skip button (opens the pause menu's skip section). */
  setExplorer(on: boolean): void {
    if (on && !this.explorerBtn) {
      const b = el('button', 'hud-btn explorer-btn', ICONS.skip);
      b.setAttribute('aria-label', 'Stage explorer: skip');
      b.addEventListener('click', () => this.onExplorerTap?.());
      this.btnRow.prepend(b);
      this.explorerBtn = b;
    }
    this.explorerBtn?.classList.toggle('hidden', !on);
  }

  /** Mobile fullscreen button (added by UI when relevant). */
  addFullscreenButton(): void {
    const fs = el('button', 'hud-btn', ICONS.fullscreen);
    fs.setAttribute('aria-label', 'Fullscreen');
    fs.addEventListener('click', () => this.onFullscreenTap?.());
    this.btnRow.prepend(fs);
  }

  /** Called by UI when a stage starts. */
  setNode(node: EvoNode, index: number): void {
    this.nameplate.style.color = node.color;
    this.nameplate.innerHTML = `<i></i><span>${escapeHtml(node.name)}</span><span class="stage-no">${index > 0 ? `Form ${index}` : ''}</span>`;
    for (const c of this.counters.values()) c.el.remove();
    this.counters.clear();
    this.toasts.innerHTML = '';
    this.setThreat(0);
  }

  setDevice(d: InputDevice): void {
    if (d === this.device) return;
    this.device = d;
    this.renderActions();
    this.evolveKey = '';
  }

  show(visible: boolean): void {
    this.root.classList.toggle('visible', visible);
  }

  /** Show/hide the regenerating shimmer explicitly (it also triggers automatically while health rises). */
  setRegen(on: boolean): void {
    this.health.root.classList.toggle('regen', on);
  }

  setHealth(current: number, max: number): void {
    const f = clamp01(current / Math.max(1e-6, max));
    // Health going up (regen / healing) → shimmer for a moment after the last increase.
    if (f > this.lastHp + 1e-4 && f < 1) {
      this.setRegen(true);
      clearTimeout(this.regenTimer);
      this.regenTimer = setTimeout(() => this.setRegen(false), 450);
    } else if (f >= 1 && this.lastHp < 1) this.setRegen(false);
    this.lastHp = f;
    this.health.set(f);
    const txt = `${Math.max(0, Math.ceil(current))}`;
    if (this.hpText.textContent !== txt) this.hpText.textContent = txt;
    const low = f > 0 && f < 0.3;
    if (low !== this.lowHp) {
      this.lowHp = low;
      this.health.root.classList.toggle('low', low);
      this.vLow.style.display = low ? 'block' : 'none';
    }
  }

  setStamina(frac: number | null): void {
    this.stamina.root.classList.toggle('hidden', frac === null);
    if (frac !== null) this.stamina.set(frac);
  }

  setGrowth(frac: number): void {
    const f = Math.round(clamp01(frac) * 200) / 200;
    if (f === this.growthFrac) return;
    const prev = this.growthFrac;
    this.growthFrac = f;
    this.evoFill.style.transform = `scaleX(${f})`;
    this.evoPct.textContent = `${Math.floor(f * 100)}%`;
    this.evo.classList.toggle('full', f >= 1);
    this.notches.forEach((n, i) => {
      const on = f >= GROWTH_NOTCHES[i] - 1e-4;
      if (on !== n.classList.contains('on')) {
        n.classList.toggle('on', on);
        if (on && prev >= 0 && f > prev) this.onGrowthNotch?.(i);
      }
    });
  }

  setAffinity(paths: EvoPath[], shares: Record<string, number>): void {
    const key = paths.map((p) => p.key).join(',');
    if (key !== this.affinityKey) {
      this.affinityKey = key;
      this.leadKey = '';
      this.mut.classList.toggle('hidden', paths.length < 2);
      this.mut.innerHTML = `<div class="lbl"><span>Mutation</span><span class="lead"></span></div>
        <div class="track">${paths.map((p) => `<i data-k="${p.key}" style="background-color:${p.color};color:${p.color}"></i>`).join('')}</div>
        <div class="legend n${paths.length}">${paths.map((p) => `<span data-k="${p.key}"><span style="color:${p.color}">${BRANCH_ICONS[p.key] ?? ICONS.dna}</span><b>${escapeHtml(p.label)}</b></span>`).join('')}</div>`;
      this.mutTip.innerHTML = paths
        .map((p) => `<div><span style="color:${p.color}">${BRANCH_ICONS[p.key] ?? ICONS.dna}</span><span><b style="color:${p.color}">${escapeHtml(p.label)}</b>${escapeHtml(p.hint)}</span></div>`)
        .join('');
      this.mut.append(this.mutTip);
    }
    if (paths.length < 2) return;
    let lead = paths[0];
    let best = -1;
    for (const p of paths) {
      const s = shares[p.key] ?? 0;
      if (s > best + 1e-6) {
        best = s;
        lead = p;
      }
      const i = this.mut.querySelector<HTMLElement>(`.track i[data-k="${p.key}"]`);
      if (i) i.style.flexGrow = String(Math.max(0.04, s));
    }
    if (lead.key !== this.leadKey) {
      this.leadKey = lead.key;
      const l = this.mut.querySelector<HTMLElement>('.lbl .lead')!;
      l.textContent = `→ ${lead.label}`;
      l.style.color = lead.color;
      this.mut.querySelectorAll<HTMLElement>('[data-k]').forEach((e) => e.classList.toggle('lead', e.dataset.k === lead.key));
    }
  }

  setObjective(text: string | null): void {
    const t = text ?? '';
    if (t === this.objectiveText) return;
    this.objectiveText = t;
    this.objective.innerHTML = t ? `<span class="pill">${escapeHtml(t)}</span>` : '';
  }

  toast(text: string, kind: ToastKind = 'info'): void {
    const now = performance.now();
    if (text === this.lastToast.text && now - this.lastToast.at < 800) return;
    this.lastToast = { text, at: now };
    const t = el('div', `toast ${kind}`, `${TOAST_ICONS[kind]}<span></span>`);
    (t.lastChild as HTMLElement).textContent = text;
    this.toasts.append(t);
    while (this.toasts.children.length > 4) this.toasts.firstChild?.remove();
    setTimeout(() => t.remove(), 3300);
  }

  setCooldown(action: ActionLabelKey, frac: number): void {
    const f = Math.round(clamp01(frac) * 50) / 50;
    const a = this.actionEls.get(action);
    if (a && a.frac !== f) {
      a.frac = f;
      a.cd.style.setProperty('--cd', String(f));
      a.root.classList.toggle('ready', f === 0);
    }
    this.onCooldown?.(action, f);
  }

  /** Visual press feedback on the action bar (UI forwards input state). */
  pressAction(action: ActionLabelKey, down: boolean): void {
    this.actionEls.get(action)?.root.classList.toggle('down', down);
  }

  setActions(labels: Partial<Record<ActionLabelKey, string>>): void {
    this.labels = { ...labels };
    this.renderActions();
    this.onActions?.(labels);
  }

  private renderActions(): void {
    const pad = this.device === 'gamepad';
    this.actionBar.innerHTML = '';
    this.actionEls.clear();
    if (!pad) {
      const grid = (k: string[]) => `<div class="keys"><span></span><kbd>${k[0]}</kbd><span></span><kbd>${k[1]}</kbd><kbd>${k[2]}</kbd><kbd>${k[3]}</kbd></div>`;
      this.actionBar.append(el('div', 'move-hint', `<div class="grids">${grid(['W', 'A', 'S', 'D'])}<i class="kor">/</i>${grid(['↑', '←', '↓', '→'])}</div><span>Move</span>`));
    }
    for (const k of ACTION_ORDER) {
      const label = this.labels[k];
      if (!label || (k === 'interact' && /^evolve$/i.test(label))) continue;
      const root = el(
        'div',
        `abtn ready${k === 'attack' ? ' primary' : ''}`,
        `<div class="ring">${ICONS[k]}<div class="cd"></div></div><div class="lbl">${escapeHtml(label)}</div><div class="keyrow">${pad ? `<kbd class="pad">${PAD_HINTS[k]}</kbd>` : hintHtml(k)}</div>`,
      );
      this.actionEls.set(k, { root, cd: root.querySelector('.cd')!, frac: 0 });
      this.actionBar.append(root);
    }
  }

  bossBar(name: string | null, frac = 1): void {
    if (!name) {
      if (this.bossName) {
        this.bossName = '';
        this.boss.classList.remove('show');
        this.root.classList.remove('boss-on');
      }
      return;
    }
    if (name !== this.bossName) {
      this.bossName = name;
      (this.boss.querySelector('.bn span') as HTMLElement).textContent = name;
      this.boss.classList.add('show');
      this.root.classList.add('boss-on');
    }
    this.bossBarEl.set(frac);
  }

  counter(key: string, label: string, value: number | string | null): void {
    let c = this.counters.get(key);
    if (value === null) {
      c?.el.remove();
      this.counters.delete(key);
      return;
    }
    const v = String(value);
    if (!c) {
      c = { el: el('div', 'chip', `<span></span><b></b>`), val: '' };
      (c.el.firstChild as HTMLElement).textContent = label;
      this.counters.set(key, c);
      this.chips.append(c.el);
    }
    if (c.val !== v) {
      const bump = c.val !== '';
      c.val = v;
      (c.el.lastChild as HTMLElement).textContent = v;
      if (bump) {
        c.el.classList.remove('bump');
        void c.el.offsetWidth;
        c.el.classList.add('bump');
      }
    }
  }

  damageFlash(intensity = 1): void {
    const d = this.vDamage;
    d.style.transition = 'none';
    d.style.opacity = String(Math.min(1, 0.35 + intensity * 0.65));
    void d.offsetWidth;
    d.style.transition = '';
    d.style.opacity = '0';
  }

  evolvePrompt(s: { visible: boolean; target?: EvoNode; path?: EvoPath; holdFrac?: number; terminal?: boolean }): void {
    const vis = s.visible && !!s.target;
    if (vis === this.evolve.classList.contains('hidden')) {
      this.evolve.classList.toggle('hidden', !vis);
      this.onEvolveVisible?.(vis);
    }
    if (!vis || !s.target) {
      this.evolveKey = '';
      this.evolveHold = -1;
      return;
    }
    const key = `${s.target.id}|${s.path?.key ?? ''}|${this.device}`;
    if (this.evolveKey !== key) {
      this.evolveKey = key;
      const c = s.path?.color ?? s.target.color;
      this.evolve.style.setProperty('--c', c);
      const how = this.device === 'touch' ? 'Tap &amp; hold' : this.device === 'gamepad' ? 'Hold <kbd class="pad">View</kbd>' : `Hold ${hintHtml('interact')}`;
      this.evolve.innerHTML = `<div class="ring">${ICONS.dna}</div><div class="txt">
        <div class="k">${s.terminal ? 'Final form' : 'Evolution ready'}</div>
        <div class="n" style="color:${s.target.color}">${escapeHtml(s.target.name)}</div>
        <div class="h">${how}${s.path ? ` · <span class="p">${escapeHtml(s.path.label)}</span>` : ''}</div></div>`;
    }
    const h = Math.round(clamp01(s.holdFrac ?? 0) * 100) / 100;
    if (h !== this.evolveHold) {
      this.evolveHold = h;
      this.evolve.style.setProperty('--hold', String(h));
      this.evolve.classList.toggle('holding', h > 0);
    }
  }

  setThreat(level: number): void {
    const l = Math.round(clamp01(level) * 20) / 20;
    if (l === this.threat) return;
    this.threat = l;
    this.vThreat.style.opacity = String(l * 0.9);
    this.threatBadge.classList.toggle('show', l >= 0.5);
  }
}
